import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { useInstituteAuth } from "../../context/InstituteAuthContext";
import { toast } from "react-toastify";
import {
  validatePassword,
  validatePasswordMatch,
} from "../../utils/passwordValidation";
import { FaEye, FaEyeSlash, FaEdit, FaSave, FaTimes } from "react-icons/fa";

// Leaflet icon setup
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const SetViewOnClick = ({ coords, shouldPan }) => {
  const map = useMap();
  useEffect(() => {
    if (shouldPan && coords[0] !== 0 && coords[1] !== 0) {
      map.setView(coords, 13);
    }
  }, [coords, map, shouldPan]);
  return null;
};

const InstituteProfileManagement = () => {
  const [formData, setFormData] = useState({
    name: "",
    type_of_institute: "",
    email: "",
    date_established: "",
    contact_person: "",
    telephone: [""],
    country: "Pakistan",
    city: "Islamabad",
    area: "",
    street: "",
    houseNo: "",
    coordinates: "33.6844, 73.0479",
    website: "",
    instagram_url: "",
    facebook_url: "",
    x_url: "",
    about_institute: "",
    company_registration_number: "",
    logo: null,
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [instituteTypes, setInstituteTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  const navigate = useNavigate();
  const { instituteUser } = useInstituteAuth();

  // Map state
  const [mapCenter, setMapCenter] = useState([33.6844, 73.0479]);
  const [marker, setMarker] = useState([33.6844, 73.0479]);
  const [shouldPan, setShouldPan] = useState(false);
  const mapRef = useRef(null);

  // Validation functions
  const validateRequiredField = (value, fieldName) => {
    if (!value || value.toString().trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const validatePhoneNumber = (phone) => {
    if (!phone.trim()) {
      return "Phone number is required";
    }
    const phoneRegex = /^[0-9+\-() ]{7,20}$/;
    if (!phoneRegex.test(phone)) {
      return "Invalid phone number format";
    }
    return "";
  };

  const validateAtLeastOnePhone = (telephones) => {
    const validPhones = telephones.filter((phone) => phone.trim() !== "");
    if (validPhones.length === 0) {
      return "At least one phone number is required";
    }
    return "";
  };

  // Fetch institute data
  useEffect(() => {
    if (!instituteUser?.id) {
      toast.error("Institute user not found");
      navigate("/institute-owner");
      return;
    }
    fetchInstituteData();
  }, [instituteUser]);

  const fetchInstituteData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/institutes/${instituteUser.id}`,
        { withCredentials: true }
      );

      const data = response.data;

      // Parse coordinates
      let coords = [33.6844, 73.0479];
      if (data.coordinates) {
        const coordParts = data.coordinates
          .split(",")
          .map((c) => parseFloat(c.trim()));
        if (
          coordParts.length === 2 &&
          !isNaN(coordParts[0]) &&
          !isNaN(coordParts[1])
        ) {
          coords = coordParts;
        }
      }

      setFormData({
        name: data.name || "",
        type_of_institute: data.type_of_institute || "",
        email: data.email || "",
        date_established: data.date_established?.split("T")[0] || "",
        contact_person: data.contact_person || "",
        telephone:
          data.telephone && data.telephone.length > 0 ? data.telephone : [""],
        country: data.country || "Pakistan",
        city: data.city || "Islamabad",
        area: data.area || "",
        street: data.street || "",
        houseNo: data.plot_no || "",
        coordinates: data.coordinates || "33.6844, 73.0479",
        website: data.website || "",
        instagram_url: data.instagram_url || "",
        facebook_url: data.facebook_url || "",
        x_url: data.x_url || "",
        about_institute: data.about_institute || "",
        company_registration_number: data.company_registration_number || "",
        logo: null,
      });

      if (data.logo) {
        setExistingLogoUrl(data.logo);
      }

      setMapCenter(coords);
      setMarker(coords);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching institute data:", error);
      toast.error("Failed to load institute data");
      setLoading(false);
    }
  };

  // Fetch institute types
  useEffect(() => {
    const fetchInstituteTypes = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/institute/get-institute-types`
        );
        setInstituteTypes(response.data);
      } catch (error) {
        console.error("Error fetching institute types:", error);
        toast.error("Failed to load institute types");
      }
    };
    fetchInstituteTypes();
  }, []);

  // Map functions
  const geocodeAddress = useCallback(async () => {
    const { country, city, area, street, houseNo } = formData;
    if (!country && !city && !area) {
      return;
    }

    const addressParts = [houseNo, street, area, city, country].filter(Boolean);
    const fullAddress = addressParts.join(", ");

    if (!fullAddress.trim()) {
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          fullAddress
        )}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newCoords = [parseFloat(lat), parseFloat(lon)];
        setMapCenter(newCoords);
        setMarker(newCoords);
        setShouldPan(true);
        setFormData((prev) => ({
          ...prev,
          coordinates: `${lat}, ${lon}`,
        }));
      } else {
        toast.warning("Address not found. Please adjust the marker manually.");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      toast.error("Failed to geocode address");
    }
  }, [formData]);

  const handleMapClick = useCallback(async (e) => {
    const { lat, lng } = e.latlng;
    setMarker([lat, lng]);
    setFormData((prev) => ({
      ...prev,
      coordinates: `${lat}, ${lng}`,
    }));

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();

      if (data && data.address) {
        const addr = data.address;
        setFormData((prev) => ({
          ...prev,
          country: addr.country || prev.country,
          city:
            addr.city ||
            addr.town ||
            addr.village ||
            addr.municipality ||
            prev.city,
          area: addr.suburb || addr.neighbourhood || addr.district || prev.area,
          street: addr.road || prev.street,
          houseNo: addr.house_number || prev.houseNo,
        }));
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    }
  }, []);

  const MapEvents = () => {
    useMap().on("click", handleMapClick);
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Logo size should be less than 5MB");
        return;
      }
      setFormData({ ...formData, logo: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTelephoneChange = (index, value) => {
    const updatedTelephones = [...formData.telephone];
    updatedTelephones[index] = value;
    setFormData({ ...formData, telephone: updatedTelephones });
  };

  const addTelephone = () => {
    setFormData({ ...formData, telephone: [...formData.telephone, ""] });
  };

  const removeTelephone = (index) => {
    if (formData.telephone.length > 1) {
      const updatedTelephones = formData.telephone.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, telephone: updatedTelephones });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Validation
    const newErrors = {};

    const nameError = validateRequiredField(formData.name, "Institute Name");
    if (nameError) newErrors.name = nameError;

    const typeError = validateRequiredField(
      formData.type_of_institute,
      "Institute Type"
    );
    if (typeError) newErrors.type_of_institute = typeError;

    const dateError = validateRequiredField(
      formData.date_established,
      "Date Established"
    );
    if (dateError) newErrors.date_established = dateError;

    const contactError = validateRequiredField(
      formData.contact_person,
      "Contact Person"
    );
    if (contactError) newErrors.contact_person = contactError;

    const phoneError = validateAtLeastOnePhone(formData.telephone);
    if (phoneError) newErrors.telephone = phoneError;

    // Validate individual phone numbers
    formData.telephone.forEach((phone, index) => {
      if (phone.trim()) {
        const phoneValidation = validatePhoneNumber(phone);
        if (phoneValidation) {
          newErrors[`telephone_${index}`] = phoneValidation;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      toast.error("Please fix all errors before submitting");
      return;
    }

    try {
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "telephone") {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === "logo") {
          if (formData.logo) {
            formDataToSend.append("logo", formData.logo);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/institutes/${instituteUser.id}/profile`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      if (response.data.message) {
        toast.success(response.data.message);
        setIsEditing(false);
        // Refresh data
        await fetchInstituteData();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Password change functions
  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (passwordErrors[field]) {
      setPasswordErrors({ ...passwordErrors, [field]: "" });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePasswordChange = () => {
    const newErrors = {};

    if (!passwordData.oldPassword) {
      newErrors.oldPassword = "Current password is required";
    }

    const newPasswordError = validatePassword(passwordData.newPassword);
    if (newPasswordError) {
      newErrors.newPassword = newPasswordError;
    }

    const confirmPasswordError = validatePasswordMatch(
      passwordData.newPassword,
      passwordData.confirmPassword
    );
    if (confirmPasswordError) {
      newErrors.confirmPassword = confirmPasswordError;
    }

    return newErrors;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validatePasswordChange();
    if (Object.keys(validationErrors).length > 0) {
      setPasswordErrors(validationErrors);
      return;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/institutes/${instituteUser.id}/password`,
        {
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        },
        { withCredentials: true }
      );

      if (response.data.message) {
        toast.success(response.data.message);
        setIsChangingPassword(false);
        setPasswordData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordErrors({});
      }
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to change password";
      toast.error(errorMessage);
      if (error.response?.data?.field) {
        setPasswordErrors({ [error.response.data.field]: errorMessage });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-[60px] mx-auto mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => navigate("/institute-owner")}
          />
          <h1 className="text-3xl font-bold text-[#3498db] mb-2">
            Institute Profile Management
          </h1>
          <p className="text-[#57606f] mt-2">
            Manage your institute information
          </p>
          <div className="flex gap-3 justify-center mt-4">
            <button
              onClick={() => navigate("/institute-owner")}
              className="px-4 py-2 h-11 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-300 font-semibold"
            >
              Back to Dashboard
            </button>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 flex items-center gap-2"
              >
                <FaEdit /> Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Password Change Section */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Password Management
            </h2>
            {!isChangingPassword && (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
              >
                Change Password
              </button>
            )}
          </div>

          {isChangingPassword && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4 mt-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    value={passwordData.oldPassword}
                    onChange={(e) =>
                      handlePasswordChange("oldPassword", e.target.value)
                    }
                    className={`w-full h-11 px-3 pr-12 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      passwordErrors.oldPassword
                        ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("old")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.old ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordErrors.oldPassword && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {passwordErrors.oldPassword}
                  </div>
                )}
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      handlePasswordChange("newPassword", e.target.value)
                    }
                    className={`w-full h-11 px-3 pr-12 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      passwordErrors.newPassword
                        ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("new")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.new ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordErrors.newPassword && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {passwordErrors.newPassword}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 8 characters with a number and special
                  character
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      handlePasswordChange("confirmPassword", e.target.value)
                    }
                    className={`w-full h-11 px-3 pr-12 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      passwordErrors.confirmPassword
                        ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("confirm")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.confirm ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {passwordErrors.confirmPassword}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-6">
                <button
                  type="submit"
                  className="px-4 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({
                      oldPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                    setPasswordErrors({});
                  }}
                  className="px-4 py-2 h-11 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-300 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Profile Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-8 space-y-8 backdrop-blur-[10px] border border-white/20"
        >
          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Institute Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institute Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.name
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter institute name"
                />
                {errors.name && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Institute Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institute Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type_of_institute"
                  value={formData.type_of_institute}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.type_of_institute
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                >
                  <option value="">Select Type</option>
                  {instituteTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.type_of_institute && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.type_of_institute}
                  </div>
                )}
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] bg-[#f8f9fa] cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed
                </p>
              </div>

              {/* Date Established */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Established <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date_established"
                  value={formData.date_established}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.date_established
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
                {errors.date_established && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.date_established}
                  </div>
                )}
              </div>

              {/* Contact Person */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.contact_person
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter contact person name"
                />
                {errors.contact_person && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.contact_person}
                  </div>
                )}
              </div>

              {/* Company Registration Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Registration Number
                </label>
                <input
                  type="text"
                  name="company_registration_number"
                  value={formData.company_registration_number}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter registration number (if applicable)"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Telephone Numbers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Contact Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telephone Numbers <span className="text-red-500">*</span>
                </label>
                {errors.telephone && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.telephone}
                  </div>
                )}
                {formData.telephone.map((phone, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) =>
                        handleTelephoneChange(index, e.target.value)
                      }
                      placeholder="0XXX-XXXXXXX or 0XX-XXXXXXX"
                      disabled={!isEditing}
                      required={index === 0}
                      className={`flex-1 h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                        errors[`telephone_${index}`]
                          ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                          : !isEditing
                          ? "border-[#ddd] bg-[#f8f9fa]"
                          : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                      }`}
                    />
                    {isEditing && index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeTelephone(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    )}
                    {isEditing && index === formData.telephone.length - 1 && (
                      <button
                        type="button"
                        onClick={addTelephone}
                        className="px-3 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Address Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <input
                  type="text"
                  name="country"
                  required
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter country"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter city"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area
                </label>
                <input
                  type="text"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter area"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter street"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House/Plot No{" "}
                  <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="houseNo"
                  value={formData.houseNo}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter house/plot number"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coordinates
                </label>
                <input
                  type="text"
                  name="coordinates"
                  value={formData.coordinates}
                  disabled
                  placeholder="Click on map or find coordinates"
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] bg-[#f8f9fa]"
                />
              </div>
            </div>

            {/* Map */}
            {isEditing && (
              <button
                type="button"
                onClick={geocodeAddress}
                className="mb-4 px-4 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                Find Coordinates from Address
              </button>
            )}

            <div className="h-96 w-full rounded-lg overflow-hidden border border-gray-300">
              <MapContainer
                center={mapCenter}
                zoom={13}
                ref={mapRef}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker position={marker} />
                <SetViewOnClick coords={mapCenter} shouldPan={shouldPan} />
                {isEditing && <MapEvents />}
              </MapContainer>
              {isEditing && (
                <p className="text-sm text-gray-600 mt-2">
                  Click on the map to update the location
                </p>
              )}
            </div>
          </div>

          {/* Social Media & Website */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Contact Information
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="https://example.com"
                    className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      !isEditing
                        ? "border-[#ddd] bg-[#f8f9fa]"
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    name="instagram_url"
                    value={formData.instagram_url}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="https://instagram.com/username"
                    className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      !isEditing
                        ? "border-[#ddd] bg-[#f8f9fa]"
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    name="facebook_url"
                    value={formData.facebook_url}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="https://facebook.com/page"
                    className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      !isEditing
                        ? "border-[#ddd] bg-[#f8f9fa]"
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  X (Twitter) URL
                </label>
                <input
                  type="url"
                  name="x_url"
                  value={formData.x_url}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="https://x.com/username"
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* About Institute */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Additional Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About the Institute{" "}
                  <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  name="about_institute"
                  value={formData.about_institute}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows="4"
                  maxLength="500"
                  className={`w-full px-3 py-2 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    !isEditing
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Tell us about your institute, its mission, vision, and achievements..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.about_institute.length}/500 characters
                </p>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Institute Logo <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center justify-center w-full">
              {existingLogoUrl || logoPreview ? (
                <div className="relative w-full">
                  <img
                    src={logoPreview || existingLogoUrl}
                    alt="Institute Logo"
                    className="w-32 h-32 object-contain border border-gray-300 rounded-lg mb-4"
                  />
                  {isEditing && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload New Logo (Max 10MB)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}
                </div>
              ) : (
                isEditing && (
                  <label
                    htmlFor="logo-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg
                        className="w-8 h-8 mb-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">
                          Click to upload logo
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG or JPEG (MAX. 10MB)
                      </p>
                    </div>
                    <input
                      id="logo-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </label>
                )
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="pt-6">
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 h-11 flex justify-center items-center border border-transparent rounded-lg text-base font-semibold text-white bg-[#3498db] hover:bg-[#2980b9] focus:outline-none transition-all duration-300 uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <FaSave className="mr-2" />
                  {isSubmitting ? "SAVING..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    fetchInstituteData();
                  }}
                  disabled={isSubmitting}
                  className="px-6 py-3 h-11 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default InstituteProfileManagement;
